import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck, RefreshCw, ChevronRight, CheckCircle2, PackageSearch, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const fmtEur = (n) => `${(Number(n) || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const fmtDate = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleDateString("de-DE"); } catch { return "—"; } };

/**
 * Jun-2026 (User-Wunsch) — Offene Garantien: alle Werkstattaufträge mit mindestens
 * einer Garantie-Position (billing_category = „warranty"), die noch nicht erledigt
 * und nicht abgerechnet sind.
 */
export default function OpenWarrantyOrdersTab() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    // Aug-2026 (User-Wunsch Jörg): nicht zugeordnete CEVA-Garantieteile
    const [parts, setParts] = useState([]);
    const [partOrder, setPartOrder] = useState({}); // item_id -> work_order_id
    const [partBusy, setPartBusy] = useState(null);
    const navigate = useNavigate();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [r, p] = await Promise.all([
                api.get("/workshop/warranty-orders/open"),
                api.get("/wms/warranty/unassigned"),
            ]);
            setOrders(r.data?.orders || []);
            setParts(p.data?.items || []);
        } catch {
            toast.error("Offene Garantien konnten nicht geladen werden");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const assignPart = async (item) => {
        const woId = partOrder[item.id] || orders[0]?.id;
        if (!woId) { toast.error("Bitte Garantie-Auftrag wählen"); return; }
        setPartBusy(item.id);
        try {
            const r = await api.post(`/wms/warranty/unassigned/${item.id}/assign`, { work_order_id: woId });
            toast.success(`„${item.name}" auf Auftrag ${r.data.order_number} gebucht`);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Zubuchen fehlgeschlagen");
        } finally {
            setPartBusy(null);
        }
    };

    const removePart = async (item) => {
        setPartBusy(item.id);
        try {
            await api.delete(`/wms/warranty/unassigned/${item.id}`);
            toast.success("Garantieteil entfernt");
            setParts((prev) => prev.filter((p) => p.id !== item.id));
        } catch {
            toast.error("Entfernen fehlgeschlagen");
        } finally {
            setPartBusy(null);
        }
    };

    return (
        <div data-testid="wo-warranty-open">
            <div className="flex items-center gap-2 mb-3">
                <ShieldCheck className="w-4 h-4 text-rose-600" />
                <h2 className="font-display font-black text-lg">Offene Garantien</h2>
                <span className="text-[10px] font-bold uppercase tracking-wider bg-[#111827] text-[#FFCC00] px-2 py-0.5 rounded-sm font-mono-num">{orders.length}</span>
                <button onClick={load} className="ml-auto text-xs font-bold uppercase tracking-wider text-gray-500 hover:text-[#111827] inline-flex items-center gap-1" data-testid="wo-warranty-open-refresh">
                    <RefreshCw className="w-3 h-3" /> Aktualisieren
                </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">Alle Aufträge mit einer <strong>Garantie-Position</strong>, die noch <strong>nicht erledigt</strong> und <strong>nicht abgerechnet</strong> sind.</p>

            {/* Aug-2026 (User-Wunsch Jörg): nicht zugeordnete CEVA-Garantieteile */}
            {parts.length > 0 && (
                <div className="mb-5 border border-amber-300 bg-amber-50 rounded-sm p-3" data-testid="wo-warranty-unassigned">
                    <div className="text-xs font-bold uppercase tracking-wider text-amber-800 mb-2 flex items-center gap-1.5">
                        <PackageSearch className="w-3.5 h-3.5" /> Nicht zugeordnete Garantieteile ({parts.length})
                    </div>
                    <p className="text-[11px] text-amber-800 mb-2">
                        Beim Lieferschein-Scan (z. B. CEVA) wurde <b>kein passender Garantie-Auftrag</b> gefunden.
                        Teile hier einem Auftrag zubuchen oder entfernen.
                    </p>
                    <ul className="space-y-2">
                        {parts.map((p) => (
                            <li key={p.id} className="bg-white border border-gray-200 rounded-sm px-3 py-2 flex flex-wrap items-center gap-2" data-testid={`wo-warranty-unassigned-${p.id}`}>
                                <div className="min-w-0 flex-1">
                                    <span className="text-[12px] font-bold text-gray-900">{p.name}</span>
                                    <span className="text-[11px] text-gray-500 ml-2">
                                        {p.sku ? `${p.sku} · ` : ""}{p.quantity} Stk
                                        {p.source ? ` · Beleg ${p.source}` : ""} · {(p.created_at || "").slice(0, 10)}
                                    </span>
                                </div>
                                <select
                                    value={partOrder[p.id] || orders[0]?.id || ""}
                                    onChange={(e) => setPartOrder((prev) => ({ ...prev, [p.id]: e.target.value }))}
                                    className="h-8 px-2 rounded-sm border border-gray-300 text-xs bg-white max-w-[260px]"
                                    data-testid={`wo-warranty-unassigned-select-${p.id}`}
                                >
                                    {orders.length === 0 && <option value="">Kein offener Garantie-Auftrag</option>}
                                    {orders.map((o) => (
                                        <option key={o.id} value={o.id}>
                                            {o.order_number}{o.customer_name ? ` · ${o.customer_name}` : ""}{o.vehicle_label ? ` · ${o.vehicle_label}` : ""}
                                        </option>
                                    ))}
                                </select>
                                <button type="button" onClick={() => assignPart(p)} disabled={partBusy === p.id || orders.length === 0}
                                    className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-white bg-emerald-700 hover:bg-emerald-800 rounded-sm px-2.5 py-1.5 disabled:opacity-50"
                                    data-testid={`wo-warranty-unassigned-assign-${p.id}`}>
                                    {partBusy === p.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldCheck className="w-3 h-3" />} Zubuchen
                                </button>
                                <button type="button" onClick={() => removePart(p)} disabled={partBusy === p.id}
                                    className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-red-700 border border-red-300 hover:bg-red-50 rounded-sm px-2.5 py-1.5 disabled:opacity-50"
                                    data-testid={`wo-warranty-unassigned-delete-${p.id}`}>
                                    <Trash2 className="w-3 h-3" /> Entfernen
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {loading ? (
                <div className="flex items-center gap-2 text-gray-500 py-8"><Loader2 className="w-4 h-4 animate-spin" /> Lade offene Garantien…</div>
            ) : orders.length === 0 ? (
                <div className="bg-white border border-gray-200 rounded-sm p-8 text-center text-gray-500" data-testid="wo-warranty-open-empty">
                    <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-emerald-500" />
                    Keine offenen Garantien — alle erledigt oder abgerechnet.
                </div>
            ) : (
                <div className="bg-white border border-gray-200 rounded-sm overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-gray-50 text-left text-[10px] uppercase tracking-wider text-gray-500">
                                <th className="px-3 py-2">Auftrag</th>
                                <th className="px-3 py-2">Fahrzeug</th>
                                <th className="px-3 py-2">Garantiegeber</th>
                                <th className="px-3 py-2">Status</th>
                                <th className="px-3 py-2 text-right">Garantie (netto)</th>
                                <th className="px-3 py-2">Angelegt</th>
                                <th className="px-3 py-2 text-right">Aktion</th>
                            </tr>
                        </thead>
                        <tbody>
                            {orders.map((o) => (
                                <tr key={o.id} onClick={() => navigate(`/dealer/workshop-orders?focus=${o.id}`)} className="border-t border-gray-100 cursor-pointer hover:bg-rose-50/60 transition-colors" title="Auftrag öffnen" data-testid={`wo-warranty-open-row-${o.id}`}>
                                    <td className="px-3 py-2 font-bold font-mono-num">{o.order_number}</td>
                                    <td className="px-3 py-2">{o.vehicle_label}</td>
                                    <td className="px-3 py-2 text-gray-600">{o.warranty_manufacturer || "—"}</td>
                                    <td className="px-3 py-2">
                                        <span className="text-[10px] font-bold uppercase tracking-wider bg-rose-100 text-rose-800 rounded-sm px-2 py-0.5">{o.status_label}</span>
                                    </td>
                                    <td className="px-3 py-2 text-right font-mono-num">{fmtEur(o.warranty_net)}</td>
                                    <td className="px-3 py-2 text-gray-600">{fmtDate(o.created_at)}</td>
                                    <td className="px-3 py-2 text-right">
                                        <button type="button" onClick={(e) => { e.stopPropagation(); navigate(`/dealer/workshop-orders?focus=${o.id}`); }}
                                            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#111827] border border-gray-300 rounded-sm px-2 py-1 bg-white hover:bg-gray-50"
                                            data-testid={`wo-warranty-open-go-${o.id}`}>
                                            Auftrag <ChevronRight className="w-3 h-3" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
